import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { dramas } from "@/mocks/dramas";
import { actors } from "@/mocks/actors";
import DramaCard from "@/components/feature/DramaCard";
import ActorCard from "@/components/feature/ActorCard";
import Breadcrumb from "@/components/feature/Breadcrumb";

const PAGE_SIZE = 6;

const episodeRanges = [
  { value: "all", label: "全部集数" },
  { value: "lt30", label: "30 集以下" },
  { value: "30to40", label: "30 - 40 集" },
  { value: "gt40", label: "40 集以上" },
];

const sortOptions = [
  { value: "latest", label: "按发布时间" },
  { value: "hot", label: "按热度" },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "actor" ? "actor" : "drama";
  const q = searchParams.get("q") || "";

  const [epRange, setEpRange] = useState("all");
  const [year, setYear] = useState("all");
  const [actorKeyword, setActorKeyword] = useState("");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  useSeo({
    title:
      tab === "actor"
        ? "风流女管家 - 演员介绍与演员表查询"
        : "风流女管家 - 剧集分类与剧情介绍",
    description:
      tab === "actor"
        ? "风流女管家演员介绍页，汇总《风流女管家》及同题材剧集的主要演员资料与代表作品，支持按姓名快速查找。"
        : "风流女管家剧集分类页，提供按集数、年份、演员等条件的筛选与排序功能，快速找到想看的剧情介绍与剧集信息。",
    keywords:
      tab === "actor"
        ? "风流女管家,演员介绍,演员表,主演资料"
        : "风流女管家,剧集分类,剧情介绍,剧集筛选",
  });

  const years = useMemo(
    () => Array.from(new Set(dramas.map((d) => d.year))).sort((a, b) => b - a),
    []
  );

  const resetPage = () => setPage(1);

  const updateQuery = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("q", value);
    else next.delete("q");
    setSearchParams(next, { replace: true });
    resetPage();
  };

  const switchTab = (value: "drama" | "actor") => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
    resetPage();
  };

  const filteredDramas = useMemo(() => {
    let list = dramas.filter((d) => {
      const matchQ =
        !q ||
        d.title.toLowerCase().includes(q.toLowerCase()) ||
        d.genre.toLowerCase().includes(q.toLowerCase()) ||
        d.tags.some((t) => t.toLowerCase().includes(q.toLowerCase()));
      const matchYear = year === "all" || String(d.year) === year;
      let matchEp = true;
      if (epRange === "lt30") matchEp = d.episodes < 30;
      else if (epRange === "30to40")
        matchEp = d.episodes >= 30 && d.episodes <= 40;
      else if (epRange === "gt40") matchEp = d.episodes > 40;
      const matchActor =
        !actorKeyword ||
        d.castIds.some((id) => {
          const actor = actors.find((a) => a.id === id);
          return actor?.name.includes(actorKeyword);
        });
      return matchQ && matchYear && matchEp && matchActor;
    });
    list = [...list].sort((a, b) =>
      sort === "hot" ? b.views - a.views : a.updateTime < b.updateTime ? 1 : -1
    );
    return list;
  }, [q, year, epRange, actorKeyword, sort]);

  const filteredActors = useMemo(() => {
    return actors.filter(
      (a) =>
        !q || a.name.includes(q) || a.englishName.toLowerCase().includes(q.toLowerCase())
    );
  }, [q]);

  const totalPages = Math.max(1, Math.ceil(filteredDramas.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedDramas = filteredDramas.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "剧集分类" },
            ]}
          />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            {tab === "actor" ? "演员介绍" : "剧情介绍合集"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {tab === "actor"
              ? "汇集《风流女管家》与同题材剧集的主要演员资料，按姓名即可快速查找。"
              : "这里收录了《风流女管家》及同题材优秀剧集的剧情介绍，支持按集数、年份与演员筛选，帮助你快速定位想看的作品。"}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6">
        {/* Tabs */}
        <div className="mb-6 inline-flex rounded-full bg-background-200 p-1">
          <button
            type="button"
            onClick={() => switchTab("drama")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              tab === "drama"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            剧集列表
          </button>
          <button
            type="button"
            onClick={() => switchTab("actor")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              tab === "actor"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            演员列表
          </button>
        </div>

        {tab === "drama" ? (
          <>
            {/* Filters */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-500">
                    剧集集数
                  </span>
                  <select
                    value={epRange}
                    onChange={(e) => {
                      setEpRange(e.target.value);
                      resetPage();
                    }}
                    className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  >
                    {episodeRanges.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-500">
                    上映年份
                  </span>
                  <select
                    value={year}
                    onChange={(e) => {
                      setYear(e.target.value);
                      resetPage();
                    }}
                    className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  >
                    <option value="all">全部年份</option>
                    {years.map((y) => (
                      <option key={y} value={String(y)}>
                        {y} 年
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-500">
                    主要演员
                  </span>
                  <input
                    value={actorKeyword}
                    onChange={(e) => {
                      setActorKeyword(e.target.value);
                      resetPage();
                    }}
                    placeholder="输入演员姓名"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-500">
                    排序方式
                  </span>
                  <select
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value);
                      resetPage();
                    }}
                    className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  >
                    {sortOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-background-200 pt-3">
                <span className="text-sm text-foreground-500">关键词：</span>
                <input
                  value={q}
                  onChange={(e) => updateQuery(e.target.value)}
                  placeholder="剧集名 / 类型"
                  className="w-40 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
                <span className="ml-auto text-sm text-foreground-500">
                  共 {filteredDramas.length} 部
                </span>
              </div>
            </div>

            {/* List */}
            {pagedDramas.length > 0 ? (
              <>
                <div className="mt-6 space-y-4">
                  {pagedDramas.map((drama) => (
                    <DramaCard key={drama.id} drama={drama} variant="row" />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="flex cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <i className="ri-arrow-left-s-line" />
                      上一页
                    </button>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPage(i + 1)}
                        className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-sm transition-colors ${
                          currentPage === i + 1
                            ? "bg-primary-500 text-background-50"
                            : "border border-background-300 text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className="flex cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      下一页
                      <i className="ri-arrow-right-s-line" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-2xl" />
                </span>
                <p className="text-sm text-foreground-600">
                  暂无符合条件的剧集，试试其他筛选条件
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setEpRange("all");
                    setYear("all");
                    setActorKeyword("");
                    updateQuery("");
                  }}
                  className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-secondary-600"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <label className="flex flex-col gap-1.5 sm:max-w-xs">
                <span className="text-xs font-medium text-foreground-500">
                  演员姓名关键词
                </span>
                <input
                  value={q}
                  onChange={(e) => updateQuery(e.target.value)}
                  placeholder="输入演员姓名"
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </label>
            </div>

            {filteredActors.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {filteredActors.map((actor) => (
                  <ActorCard key={actor.id} actor={actor} />
                ))}
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-user-unfollow-line text-2xl" />
                </span>
                <p className="text-sm text-foreground-600">
                  暂无符合条件的演员，试试其他关键词
                </p>
                <Link
                  to="/category?tab=actor"
                  className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-secondary-600"
                >
                  查看全部演员
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}